import { usePersistentCallback } from '@prophecy/utils/react/hooks';
import { isEqual } from 'lodash-es';
import { nanoid } from 'nanoid';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/** A prompt submitted while the agent was busy with this conversation's current turn. */
export type QueuedHomeChatPrompt = {
  id: string;
  text: string;
  /** Already-resolved mention/file context, same shape `session.send` takes — files are uploaded
   * to Databox as soon as they're attached (see useHomeChatAttachments), so by enqueue time this is
   * already a plain, JSON-serializable set of `@file:`/`@project:` refs, not raw File bytes. */
  context?: Record<string, unknown>;
  createdAt: number;
};

export type QueuedHomeChatPromptInput = {
  text: string;
  context?: Record<string, unknown>;
};

const QUEUED_HOME_CHAT_PROMPT_STORAGE_PREFIX = 'prophecy:homeChatQueue';

export function getQueuedHomeChatPromptStorageKey(conversationId: string): string {
  return `${QUEUED_HOME_CHAT_PROMPT_STORAGE_PREFIX}:${conversationId}`;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object';
}

/** Defensive parse: localStorage is shared across tabs/app versions, so drop rows that don't match. */
function parseStored(raw: string | null): QueuedHomeChatPrompt[] {
  if (!raw) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  return parsed.flatMap<QueuedHomeChatPrompt>((item) => {
    if (
      !isObject(item) ||
      typeof item.id !== 'string' ||
      typeof item.text !== 'string' ||
      typeof item.createdAt !== 'number'
    ) {
      return [];
    }
    return [
      {
        id: item.id,
        text: item.text,
        createdAt: item.createdAt,
        context: isObject(item.context) ? (item.context as Record<string, unknown>) : undefined
      }
    ];
  });
}

function readQueue(conversationId: string | undefined): QueuedHomeChatPrompt[] {
  if (!conversationId || typeof window === 'undefined') return [];
  try {
    return parseStored(window.localStorage.getItem(getQueuedHomeChatPromptStorageKey(conversationId)));
  } catch {
    return [];
  }
}

function writeQueue(conversationId: string | undefined, queue: QueuedHomeChatPrompt[]) {
  if (!conversationId || typeof window === 'undefined') return;
  try {
    if (queue.length === 0) {
      window.localStorage.removeItem(getQueuedHomeChatPromptStorageKey(conversationId));
    } else {
      window.localStorage.setItem(getQueuedHomeChatPromptStorageKey(conversationId), JSON.stringify(queue));
    }
  } catch {
    // Quota exceeded / private mode — degrade silently to in-memory only.
  }
}

export type UseQueuedHomeChatPromptsResult = {
  queue: QueuedHomeChatPrompt[];
  enqueue: (input: QueuedHomeChatPromptInput) => void;
  removeAt: (id: string) => void;
  restoreToFront: (prompt: QueuedHomeChatPrompt) => void;
  getById: (id: string) => QueuedHomeChatPrompt | undefined;
  clear: () => void;
};

/**
 * Per-conversation FIFO queue of prompts submitted while the agent is busy. Persisted to
 * localStorage so a reload shows the same queued state back (files aren't restaged — their
 * already-uploaded `@file:` paths live in `context` and round-trip as plain JSON).
 */
export function useQueuedHomeChatPrompts(conversationId: string | undefined): UseQueuedHomeChatPromptsResult {
  const [queue, setQueue] = useState<QueuedHomeChatPrompt[]>(() => readQueue(conversationId));
  const conversationIdRef = useRef(conversationId);
  // Live in-memory source of truth for the stable mutators. Reading localStorage here instead
  // would strand prompts whenever writeQueue silently fails (quota / private mode).
  const queueRef = useRef(queue);

  // Re-hydrate on conversation change (other conversations' queues stay on disk).
  useEffect(() => {
    conversationIdRef.current = conversationId;
    const hydrated = readQueue(conversationId);
    if (isEqual(hydrated, queueRef.current)) return;
    queueRef.current = hydrated;
    setQueue(hydrated);
  }, [conversationId]);

  const persist = useCallback((next: QueuedHomeChatPrompt[]) => {
    queueRef.current = next;
    writeQueue(conversationIdRef.current, next);
    setQueue(next);
  }, []);

  const enqueue = usePersistentCallback((input: QueuedHomeChatPromptInput) => {
    // A queue belongs to a conversation; there's nothing to key it on before one exists.
    if (!conversationIdRef.current) return;
    const next: QueuedHomeChatPrompt[] = [
      ...queueRef.current,
      { id: nanoid(), createdAt: Date.now(), text: input.text, context: input.context }
    ];
    persist(next);
  });

  const removeAt = usePersistentCallback((id: string) => {
    persist(queueRef.current.filter((prompt) => prompt.id !== id));
  });

  const restoreToFront = usePersistentCallback((prompt: QueuedHomeChatPrompt) => {
    persist([prompt, ...queueRef.current.filter((queued) => queued.id !== prompt.id)]);
  });

  const getById = usePersistentCallback((id: string): QueuedHomeChatPrompt | undefined => {
    return queueRef.current.find((prompt) => prompt.id === id);
  });

  const clear = usePersistentCallback(() => persist([]));

  return useMemo(
    () => ({ queue, enqueue, removeAt, restoreToFront, getById, clear }),
    [queue, enqueue, removeAt, restoreToFront, getById, clear]
  );
}
